import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { formatCurrency, formatDate } from '../../lib/formatters.js';
import { VerificationSummary } from '../submit/components/VerificationSummary.js';
import { useDemoMode } from '../../context/DemoModeContext.js';

interface EvidenceDetail {
  id: string;
  evidenceType: string;
  fileName: string;
  mimeType?: string | null;
  extractionStatus: string;
  extractedFields?: Record<string, { value: unknown; confidence: number }> | null;
  extractionConfidence?: string | null;
  isHandwritten: boolean;
  uploadedAt: string;
}

interface Check {
  checkType: string;
  outcome: string;
  confidence: number;
  reasoning: string;
}

interface Decision {
  stepNumber: number;
  decision: string;
  approvedAmount?: string | null;
  isoCurrencyCode?: string | null;
  comments?: string | null;
  decidedByType: string;
  decidedAt: string;
  acceptedAiProposal: boolean;
}

interface ClaimDetail {
  id: string;
  claimNumber: string;
  claimType: string;
  status: string;
  publicStatus?: string | null;
  publicStatusUpdatedAt?: string | null;
  sourceInstrumentType: string;
  periodStart: string;
  periodEnd: string;
  claimedAmount: string;
  computedEntitlement?: string | null;
  approvedAmount?: string | null;
  isoCurrencyCode: string;
  verificationConfidence?: string | null;
  verificationSummary?: {
    verdict?: string;
    reasoning?: string;
    bundleReasoning?: string;
    recommendedAmount?: number | null;
    checks?: Check[];
  } | null;
  notes?: string | null;
  submittedAt?: string | null;
  paymentQueuedAt?: string | null;
  settledAt?: string | null;
  createdAt: string;
  evidence: EvidenceDetail[];
  verificationChecks: Check[];
  decisions: Decision[];
  outletScopes: Array<{ outletId: string; outlet: { name: string; city?: string | null } }>;
  approverComment?: string | null;
  settlement?: {
    settlementMethod: string;
    amount: string;
    isoCurrencyCode: string;
    status: string;
    bankReference?: string | null;
    initiatedAt: string;
    completedAt?: string | null;
    batch?: { id: string; bankFormat: string | null; status: string } | null;
  } | null;
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  SUBMITTED: 'bg-copper-soft text-copper-deep',
  AI_PROCESSING: 'bg-copper-soft text-copper',
  AWAITING_REVIEW: 'bg-copper-soft text-copper-deep',
  AUTO_APPROVED: 'bg-success/10 text-success',
  APPROVED: 'bg-success/10 text-success',
  REJECTED: 'bg-danger/10 text-danger',
  PAYMENT_QUEUED: 'bg-success/10 text-success',
  PAID: 'bg-success/20 text-success',
};

const PUBLIC_STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  PENDING_REVIEW: 'bg-copper-soft text-copper-deep',
  APPROVED: 'bg-success/10 text-success',
  PARTIALLY_APPROVED: 'bg-success/10 text-success',
  REJECTED: 'bg-danger/10 text-danger',
  PAID: 'bg-success/20 text-success',
  ADDITIONAL_INFO_NEEDED: 'bg-copper/20 text-copper-deep',
};

function FieldRow({ label, value, confidence }: { label: string; value: unknown; confidence: number }) {
  const pct = Math.round(confidence * 100);
  const color = pct >= 80 ? 'text-success' : pct >= 50 ? 'text-copper' : 'text-danger';
  return (
    <div className="flex items-start gap-2 text-sm py-1 border-b border-hairline last:border-0">
      <span className="text-slate-500 w-36 shrink-0 text-xs mt-0.5">{label}</span>
      <span className="flex-1 font-mono text-xs break-all">{String(value ?? '—')}</span>
      <span className={['font-mono text-xs', color].join(' ')}>{pct}%</span>
    </div>
  );
}

export function ClaimDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { persona } = useDemoMode();
  const isDistributor = persona.role === 'distributor_submitter';
  const [claim, setClaim] = useState<ClaimDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    api.get<ClaimDetail>(`/claims/${id}`)
      .then(setClaim)
      .catch(() => setClaim(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="text-slate-400 text-sm py-8">Loading...</p>;
  if (!claim) return <p className="text-danger text-sm py-8">{t('errors.notFound')}</p>;

  // Distributors see publicStatus; brand/approver see internal status
  const displayStatus = isDistributor ? (claim.publicStatus ?? claim.status) : claim.status;
  const colorMap = isDistributor ? PUBLIC_STATUS_COLORS : STATUS_COLORS;
  const statusI18nNs = isDistributor ? 'claims.publicStatus' : 'wizard.status';

  const verSummary = claim.verificationSummary;
  const hasVerification = !isDistributor && claim.verificationChecks.length > 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate('/claims')}
            className="text-xs text-slate-500 hover:text-copper mb-2 transition-colors"
          >
            ← {t('claims.title')}
          </button>
          <h1 className="font-serif italic text-3xl text-ink">{claim.claimNumber}</h1>
          <p className="text-slate-500 text-sm mt-1">
            {t(`wizard.claimTypes.${claim.claimType}.label`, { defaultValue: claim.claimType })}
          </p>
        </div>
        <span className={['text-sm font-semibold px-3 py-1 rounded-sm', colorMap[displayStatus] ?? 'bg-slate-100 text-slate-600'].join(' ')}>
          {t(`${statusI18nNs}.${displayStatus}`, { defaultValue: displayStatus })}
        </span>
      </div>

      {/* Distributor: info-needed callout */}
      {isDistributor && displayStatus === 'ADDITIONAL_INFO_NEEDED' && claim.approverComment && (
        <div className="rounded border border-copper/40 bg-copper/5 px-4 py-3 space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-copper-deep">
            {t('claims.detail.additionalInfoNeeded')}
          </p>
          <p className="text-sm text-ink leading-relaxed">{claim.approverComment}</p>
          <button
            type="button"
            onClick={() => navigate(`/submit/new?resubmit=${claim.id}`)}
            className="mt-2 text-xs font-semibold text-copper hover:underline"
          >
            {t('claims.detail.editAndResubmit')}
          </button>
        </div>
      )}

      {/* Distributor: rejection reason */}
      {isDistributor && displayStatus === 'REJECTED' && claim.approverComment && (
        <div className="rounded border border-danger/30 bg-danger/5 px-4 py-3 space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-danger">
            {t('claims.detail.rejectionReason')}
          </p>
          <p className="text-sm text-ink leading-relaxed">{claim.approverComment}</p>
        </div>
      )}

      {/* Claim summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: t('wizard.labels.claimedAmount'), value: formatCurrency(Number(claim.claimedAmount), claim.isoCurrencyCode) },
          ...(!isDistributor ? [{ label: t('wizard.labels.computedEntitlement'), value: claim.computedEntitlement ? formatCurrency(Number(claim.computedEntitlement), claim.isoCurrencyCode) : '—' }] : []),
          { label: t('wizard.labels.period'), value: `${formatDate(claim.periodStart, 'short')} — ${formatDate(claim.periodEnd, 'short')}` },
          { label: t('wizard.labels.submittedAt'), value: claim.submittedAt ? formatDate(claim.submittedAt) : '—' },
        ].map((item) => (
          <div key={item.label} className="p-3 border border-hairline rounded bg-bone">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{item.label}</p>
            <p className="font-mono text-sm font-medium text-ink">{item.value}</p>
          </div>
        ))}
      </div>

      {/* Submitter notes (own notes — always visible) */}
      {claim.notes && (
        <div className="rounded bg-slate-100 px-4 py-3 space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{t('approver.review.notesFromSubmitter')}</p>
          <p className="text-sm text-slate-700 leading-relaxed">{claim.notes}</p>
        </div>
      )}

      {/* Outlets */}
      {claim.outletScopes.length > 0 && (
        <div className="border border-hairline rounded p-4 bg-bone">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{t('wizard.labels.outlets')}</p>
          <div className="flex flex-wrap gap-2">
            {claim.outletScopes.map((s) => (
              <span key={s.outletId} className="px-2 py-1 bg-bone-soft border border-hairline rounded text-xs">
                {s.outlet.name}{s.outlet.city ? ` — ${s.outlet.city}` : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Evidence */}
      {claim.evidence.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">{t('wizard.labels.documents')}</p>
          <div className="space-y-3">
            {claim.evidence.map((e) => (
              <div key={e.id} className="border border-hairline rounded p-4 bg-bone">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="font-medium text-sm">{e.fileName}</span>
                    <span className="text-xs text-slate-500 ml-2">{e.evidenceType}</span>
                    {e.isHandwritten && <span className="text-xs text-copper ml-2">{t('extraction.handwritten')}</span>}
                  </div>
                  <div className="text-right">
                    <span className={['text-xs px-1.5 py-0.5 rounded-sm', e.extractionStatus === 'COMPLETED' ? 'bg-success/10 text-success' : e.extractionStatus === 'FAILED' ? 'bg-danger/10 text-danger' : 'bg-slate-100 text-slate-500'].join(' ')}>
                      {e.extractionStatus}
                    </span>
                    {!isDistributor && e.extractionConfidence != null && (
                      <p className="text-xs font-mono text-slate-400 mt-0.5">{Math.round(Number(e.extractionConfidence) * 100)}%</p>
                    )}
                  </div>
                </div>
                {!isDistributor && e.extractedFields && Object.keys(e.extractedFields).length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-500 mb-1">{t('wizard.labels.extractedFields')}</p>
                    <div className="bg-bone-soft border border-hairline rounded p-2">
                      {Object.entries(e.extractedFields).map(([k, v]) => (
                        <FieldRow
                          key={k}
                          label={k}
                          value={typeof v?.value === 'object' ? JSON.stringify(v?.value) : v?.value}
                          confidence={v?.confidence ?? 0}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verification summary — brand/approver only */}
      {hasVerification && verSummary && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">{t('wizard.labels.aiSummary')}</p>
          <VerificationSummary
            verdict={verSummary.verdict ?? 'NEEDS_REVIEW'}
            confidence={Number(claim.verificationConfidence ?? 0)}
            reasoning={verSummary.bundleReasoning ?? verSummary.reasoning ?? ''}
            recommendedAmount={verSummary.recommendedAmount}
            isoCurrencyCode={claim.isoCurrencyCode}
            checks={claim.verificationChecks.map((c) => ({ ...c, confidence: Number(c.confidence) }))}
          />
        </div>
      )}

      {/* Decisions — brand/approver only */}
      {!isDistributor && claim.decisions.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Decisions</p>
          <div className="space-y-2">
            {claim.decisions.map((d, i) => (
              <div key={i} className={['p-3 border rounded', d.decision === 'AUTO_APPROVE' ? 'border-success/30 bg-success/5' : 'border-hairline bg-bone'].join(' ')}>
                <div className="flex items-start justify-between text-sm">
                  <div>
                    <span className="font-semibold">{d.decision}</span>
                    <span className="text-xs text-slate-500 ml-2">{d.decidedByType}</span>
                  </div>
                  {d.approvedAmount && (
                    <span className="font-mono text-sm">{formatCurrency(Number(d.approvedAmount), d.isoCurrencyCode ?? claim.isoCurrencyCode)}</span>
                  )}
                </div>
                {d.comments && <p className="text-xs text-slate-500 mt-1">{d.comments}</p>}
                <p className="text-xs text-slate-400 mt-1">{formatDate(d.decidedAt)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settlement section */}
      {(claim.settlement || claim.status === 'PAYMENT_QUEUED' || claim.status === 'PAID' || displayStatus === 'PAID') && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">{t('settlement.detail.title')}</p>
          <div className={['p-4 border rounded', (claim.status === 'PAID' || displayStatus === 'PAID') ? 'border-success/30 bg-success/5' : 'border-copper/30 bg-copper/5'].join(' ')}>
            {claim.settlement ? (
              <div className="space-y-2 text-sm">
                <div className="flex flex-wrap gap-x-6 gap-y-1">
                  {!isDistributor && (
                    <div>
                      <span className="text-xs text-slate-500 uppercase tracking-wide mr-1">{t('settlement.detail.method')}</span>
                      <span className="font-medium">{t(`settlement.methods.${claim.settlement.settlementMethod}`, { defaultValue: claim.settlement.settlementMethod })}</span>
                      {claim.settlement.batch?.bankFormat && (
                        <span className="text-xs text-slate-500 ml-1">({claim.settlement.batch.bankFormat})</span>
                      )}
                    </div>
                  )}
                  <div>
                    <span className="text-xs text-slate-500 uppercase tracking-wide mr-1">{t('settlement.detail.amount')}</span>
                    <span className="font-mono font-medium">{formatCurrency(Number(claim.settlement.amount), claim.settlement.isoCurrencyCode)}</span>
                  </div>
                </div>
                {!isDistributor && claim.settlement.bankReference && (
                  <p className="text-xs text-slate-500">
                    {t('settlement.detail.reference')}: <span className="font-mono text-ink">{claim.settlement.bankReference}</span>
                  </p>
                )}
                {claim.paymentQueuedAt && (
                  <p className="text-xs text-slate-500">{t('settlement.detail.queuedAt')}: {formatDate(claim.paymentQueuedAt)}</p>
                )}
                {claim.settledAt && (
                  <p className="text-xs text-success font-medium">{t('settlement.detail.settledAt')}: {formatDate(claim.settledAt)}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">{t('settlement.detail.queued')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
